import React, { useState, useRef, useEffect } from "react";

import {
  RideIconButton,
  RideGlIndeterminate,
  RideGlPlus,
} from "@rimac-seguros/ride-system-components";

import "./styles.scss";

export const SliderRange = ({
  list,
  defaultValue,
  defaultTextToSelected = "",
  disabled,
  onSelect,
  isGreatterValue,
}) => {
  const [position, setPosition] = useState(1);
  const [initialized, setInitialized] = useState(false);
  const [dragOffset, setDragOffset] = useState(null);

  const sliderRef = useRef(null);
  const isDragging = useRef(false);
  const positionRef = useRef(1);
  const dragStartPosition = useRef(1);
  const dragOffsetRef = useRef(null);

  const numItems = list.length;

  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  useEffect(() => {
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  useEffect(() => {
    if (isDragging.current) return;

    if (list.length > 0 && initialized) {
      onSelect(list[position - 1]);
    }
  }, [position, initialized]);

  useEffect(() => {
    if (defaultValue && !initialized) {
      const listLength = list.length;
      const index = list.findIndex((item) => item.text === defaultValue.text);
      const newIndex = isGreatterValue ? 1 : listLength;
      const newPosition = index < 0 ? newIndex : index + 1;
      setPosition(newPosition);
      setInitialized(true);
    }
  }, [defaultValue, initialized, list, isGreatterValue]);

  const handleSelect = (newPosition, e) => {
    if (disabled) return;

    const stepElement = e.target.getBoundingClientRect();
    const clickPosition = e.clientX - stepElement.left;
    const stepWidth = stepElement.width;
    const percentageClick = (clickPosition / stepWidth) * 100;

    if (newPosition === 0) {
      if (position !== 1) {
        setPosition(1);
      }
      return;
    }

    const newPos = percentageClick > 50 ? newPosition + 1 : newPosition;

    if (newPos !== position) {
      setPosition(newPos);
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;

    const sliderRect = sliderRef.current.getBoundingClientRect();
    const positionX = e.clientX - sliderRect.left;
    updatePosition(positionX, sliderRect.width);
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current) return;

    const sliderRect = sliderRef.current.getBoundingClientRect();
    const touch = e.touches[0];
    const positionX = touch.clientX - sliderRect.left;
    updatePosition(positionX, sliderRect.width);
  };

  const updatePosition = (positionX, contentWidth) => {
    if (disabled) return;

    const clamped = Math.max(0, Math.min(positionX, contentWidth));
    dragOffsetRef.current = clamped;
    setDragOffset(clamped);
  };

  const snapToStep = () => {
    if (!sliderRef.current || dragOffsetRef.current == null) return;

    const contentWidth = sliderRef.current.getBoundingClientRect().width;
    const stepWidth = contentWidth / numItems;
    const rawStep = dragOffsetRef.current / stepWidth;

    const snapped = Math.max(
      1,
      Math.min(Math.round(rawStep + 0.0001), numItems),
    );

    dragOffsetRef.current = null;
    setDragOffset(null);
    setPosition(snapped);
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    isDragging.current = true;
    dragStartPosition.current = positionRef.current;
    document.body.style.cursor = "grabbing";
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    isDragging.current = true;
    dragStartPosition.current = positionRef.current;
    document.body.style.cursor = "grabbing";
    document.addEventListener("touchmove", handleTouchMove);
    document.addEventListener("touchend", handleTouchEnd);
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    document.body.style.cursor = "";
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);

    snapToStep();
  };

  const handleTouchEnd = () => {
    isDragging.current = false;
    document.body.style.cursor = "";
    document.removeEventListener("touchmove", handleTouchMove);
    document.removeEventListener("touchend", handleTouchEnd);

    snapToStep();
  };

  const calculatePositionThumb = () => {
    if (dragOffset != null) {
      return `${dragOffset - 20}px`;
    }
    const lastPosition = numItems;
    return `calc(${(position / numItems) * 100}% + ${position *
      (3 / (numItems - 1))}px - 20px${
      lastPosition === position ? " - 13px" : ""
    })`;
  };

  const calculateTrackFillWidth = () => {
    if (dragOffset != null) {
      return `${dragOffset}px`;
    }
    return `${(position / numItems) * 100}%`;
  };

  const handlePrevValue = () => {
    if (disabled) return;
    if (position > 1) {
      const newPos = position - 1;
      setPosition(newPos);
    }
  };

  const handleNextValue = () => {
    if (disabled) return;
    if (position < numItems) {
      const newPos = position + 1;
      setPosition(newPos);
    }
  };

  return (
    <div className="flex items-center gap-ride-gl-spacing-xlarge mb-7">
      <RideIconButton
        glyph={<RideGlIndeterminate />}
        variant="outline"
        size="small"
        disabled={position <= 1}
        className={`slider-button ${position > 1 ? "active" : ""}`}
        onClick={handlePrevValue}
      />
      <div
        ref={sliderRef}
        className={`slider-range ${disabled ? "disabled" : ""}`}
      >
        <div className="slider-track">
          <div
            className="slider-track-fill"
            style={{ width: calculateTrackFillWidth() }}
          />
          {list.map((_, i) => (
            <div
              key={`slide-${i}`}
              className="slider-step"
              style={{
                width: `calc(100% / ${numItems})`,
              }}
              onClick={(e) => handleSelect(i, e)}
            >
              <div className="step-wrapper" />
              <div className="step-bar" />
              <div
                className={`text-right mt-ride-gl-spacing-2xlarge w-max ml-auto absolute right-0 ${
                  position - 1 === i
                    ? "ride-sys-text-paragraph-small-bold text-ride-sys-color-text-extreme transform translate-x-1/2 lg:ride-sys-text-paragraph-medium-bold"
                    : "ride-sys-text-paragraph-small-default text-ride-sys-color-text-medium lg:ride-sys-text-paragraph-medium-default"
                }`}
                style={{
                  right:
                    position - 1 === i && position === numItems ? "10px" : "0",
                }}
              >
                {_.text}
                {defaultTextToSelected &&
                  position - 1 === i &&
                  ` ${defaultTextToSelected}`}
              </div>
            </div>
          ))}
        </div>
        <div
          className="slider-handle"
          style={{
            left: calculatePositionThumb(),
          }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          <div className="slider-thumb">
            <div className="slider-thumb-dot" />
          </div>
        </div>
      </div>
      <RideIconButton
        glyph={<RideGlPlus />}
        variant="outline"
        size="small"
        disabled={position >= numItems}
        className={`slider-button ${position < numItems ? "active" : ""}`}
        onClick={handleNextValue}
      />
    </div>
  );
};
